import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import {useResetPasswordFormViewModel} from "../../view-model/reset-password-form-view-model";
import {observer} from "mobx-react-lite";
import PasswordInputWithValidation from '../../components/generics/password-input-with-validation';
import ConfirmPasswordInput from '../../components/generics/confirm-password-input';
import { useTranslation } from 'react-i18next';

const  ResetPasswordForm: React.FC = observer(() => {
  const viewModel = useResetPasswordFormViewModel()
  const hasVerified = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();

  // États de validation
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isConfirmPasswordValid, setIsConfirmPasswordValid] = useState(false);

  const { t, i18n } = useTranslation();

  const currentLang = i18n.language;

  // Vérifier si le formulaire est valide
  const isFormValid = () => {
    return isPasswordValid && isConfirmPasswordValid && viewModel.getTokenParam !== null;
  };


  // Récupérer le token et l'email depuis l'URL
  useEffect(() => {
    // Empêcher les appels multiples en mode développement (React Strict Mode)
    if (hasVerified.current) return;

    const searchParams = new URLSearchParams(location.search);
    const tokenParam = searchParams.get('token');

    if (tokenParam) {
      hasVerified.current = true;
      viewModel.setToken(tokenParam);
    }
  }, [location, viewModel]);

  // Rendu pour vérification en cours
  if (viewModel.getTokenStatus === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl border border-gray-100 relative overflow-hidden">
          <div className="flex justify-center">
            <Link to={`/${currentLang}`}>
              <img src="/prestaslot_logo.svg" alt="PrestaSlot" className="h-12 mx-auto" />
            </Link>
          </div>

          <div className="text-center relative z-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">{t('resetPassword.verifying')}</p>
          </div>

          <div className="absolute top-0 right-0 -mt-10 -mr-10 bg-primary/10 w-40 h-40 rounded-full blur-3xl z-0"></div>
          <div className="absolute bottom-0 left-0 -mb-10 -ml-10 bg-primary/5 w-40 h-40 rounded-full blur-3xl z-0"></div>
        </div>
      </div>
    );
  }

  // Rendu pour token déjà utilisé
  if (viewModel.getTokenStatus === 'already-used' || viewModel.getTokenStatus === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl border border-gray-100 relative overflow-hidden">
          <div className="flex justify-center">
            <Link to={`/${currentLang}`}>
              <img src="/prestaslot_logo.svg" alt="PrestaSlot" className="h-12 mx-auto" />
            </Link>
          </div>

          <div className="text-center relative z-10">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('resetPassword.alreadyUsedTitle')}</h1>
            <p className="text-gray-600 mb-6">{t('resetPassword.alreadyUsedText')}</p>
            <button
              onClick={() => navigate(`/${currentLang}/forgot-password`)}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200 mb-3"
            >
              {t('resetPassword.requestNewLink')}
            </button>
            <button
              onClick={() => navigate(`/${currentLang}/login`)}
              className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
            >
              {t('resetPassword.backToLogin')}
            </button>
          </div>

          <div className="absolute top-0 right-0 -mt-10 -mr-10 bg-primary/10 w-40 h-40 rounded-full blur-3xl z-0"></div>
          <div className="absolute bottom-0 left-0 -mb-10 -ml-10 bg-primary/5 w-40 h-40 rounded-full blur-3xl z-0"></div>
        </div>
      </div>
    );
  }

  // Rendu pour token expiré
  if (viewModel.getTokenStatus === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl border border-gray-100 relative overflow-hidden">
          <div className="flex justify-center">
            <Link to={`/${currentLang}`}>
              <img src="/prestaslot_logo.svg" alt="PrestaSlot" className="h-12 mx-auto" />
            </Link>
          </div>

          <div className="text-center relative z-10">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('resetPassword.expiredTitle')}</h1>
            <p className="text-gray-600 mb-6">{t('resetPassword.expiredText')}</p>
            <button
              onClick={() => navigate(`/${currentLang}/forgot-password`)}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200 mb-3"
            >
              {t('resetPassword.requestNewLink')}
            </button>
            <button
              onClick={() => navigate(`/${currentLang}/login`)}
              className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
            >
              {t('resetPassword.backToLogin')}
            </button>
          </div>

          <div className="absolute top-0 right-0 -mt-10 -mr-10 bg-primary/10 w-40 h-40 rounded-full blur-3xl z-0"></div>
          <div className="absolute bottom-0 left-0 -mb-10 -ml-10 bg-primary/5 w-40 h-40 rounded-full blur-3xl z-0"></div>
        </div>
      </div>
    );
  }

  if (viewModel.getSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl border border-gray-100 relative overflow-hidden">
          <div className="flex justify-center">
            <Link to={`/${currentLang}`}>
              <img src="/prestaslot_logo.svg" alt="PrestaSlot" className="h-12 mx-auto" />
            </Link>
          </div>

          <div className="text-center relative z-10">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('resetPassword.successTitle')}</h1>
            <p className="text-primary mb-4">
              {t('resetPassword.successText')}
            </p>
            <p className="text-gray-600 mb-6">{t('resetPassword.connectText')}</p>
            <button
              onClick={() => navigate(`/${currentLang}/login`)}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
            >
              {t('resetPassword.connectButton')}
            </button>
          </div>

          <div className="absolute top-0 right-0 -mt-10 -mr-10 bg-primary/10 w-40 h-40 rounded-full blur-3xl z-0"></div>
          <div className="absolute bottom-0 left-0 -mb-10 -ml-10 bg-primary/5 w-40 h-40 rounded-full blur-3xl z-0"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl border border-gray-100 relative overflow-hidden">
        <div className="flex justify-center">
          <Link to={`/${currentLang}`}>
            <img src="/prestaslot_logo.svg" alt="PrestaSlot" className="h-12 mx-auto" />
          </Link>
        </div>
        
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">{t('resetPassword.forgotPasswordTitle')}</h2>
          <p className="text-gray-600 text-center mb-6">{t('resetPassword.forgotPasswordText')}</p>
          
          {!viewModel.getTokenParam && (
            <div className="bg-amber-50 p-4 rounded-md mb-6">
              <p className="text-sm text-amber-600">{t('resetPassword.tokenMissing')}</p>
            </div>
          )}
          
          <form onSubmit={(e) => viewModel.handleForgotPassword(e)} className="space-y-6">
            <div className="backdrop-blur-sm bg-white/80 rounded-lg p-2">
              <PasswordInputWithValidation
                label="Nouveau mot de passe"
                id="password"
                name="password"
                value={viewModel.getNewPassword}
                onChange={(e) => viewModel.setNewPassword(e.target.value)}
                required
                placeholder="Votre mot de passe"
                autoComplete="new-password"
                onValidationChange={setIsPasswordValid}
                forceLightMode={true}
              />
            </div>
            
            <div className="backdrop-blur-sm bg-white/80 rounded-lg p-2">
              <ConfirmPasswordInput
                label="Confirmer le mot de passe"
                id="confirmPassword"
                name="confirmPassword"
                value={viewModel.getConfirmNewPassword}
                password={viewModel.getNewPassword}
                onChange={(e) => viewModel.setConfirmNewPassword(e.target.value)}
                required
                placeholder="Confirmer votre mot de passe"
                autoComplete="new-password"
                onValidationChange={setIsConfirmPasswordValid}
                forceLightMode={true}
              />
            </div>
            
            {viewModel.getErrorMessage && (
              <div className="bg-red-50 p-4 rounded-md">
                <p className="text-sm text-red-600">{viewModel.getErrorMessage}</p>
              </div>
            )}
            
            <button 
              type="submit" 
              disabled={viewModel.getisLoading || !isFormValid()}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {viewModel.getisLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('resetPassword.resettingPassword')}
                </>
              ) : (
                t('resetPassword.resetPassword')
              )}
            </button>
            
            <div className="text-center mt-4">
              <Link to={`/${currentLang}/login`} className="text-sm text-primary hover:text-primary-dark transition-colors duration-200">
                {t('resetPassword.backToLogin')}
              </Link>
            </div>
          </form>
        </div>
        
        <div className="absolute top-0 right-0 -mt-10 -mr-10 bg-primary/10 w-40 h-40 rounded-full blur-3xl z-0"></div>
        <div className="absolute bottom-0 left-0 -mb-10 -ml-10 bg-primary/5 w-40 h-40 rounded-full blur-3xl z-0"></div>
      </div>
    </div>
  );
})

export default ResetPasswordForm;
